import React from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';

import { RMWContext } from 'app/shared/providers/RMWContext';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';
import PaidInclusionActions from 'app/shared/actions/PaidInclusionActions';
import PaidInclusionSelectors from 'app/shared/selectors/PaidInclusionSelectors';
import PropertiesSelectors from 'app/shared/selectors/PropertiesSelectors';
import UserSelectors from 'app/shared/selectors/UserSelectors';
import PaidInclusionConstants from 'app/shared/constants/PaidInclusionConstants';
import SelectPaymentMethod from 'app/shared/components/paidInclusion/SelectPaymentMethod';
import CreditCardCapture from 'app/shared/components/paidInclusion/CreditCardCapture';
import CreditCardCharge from 'app/shared/components/paidInclusion/CreditCardCharge';
import PaidInclusionError from 'app/shared/components/paidInclusion/PaidInclusionError';

const PaidInclusionPaymentModal = ({
    isFormLoaded,
    isCapturing,
    selectedProductType,
    isCaptured,
    isCharging,
    isCharged,
    defaultPaymentMethodSuccess,
    defaultPaymentMethod,
    apiAccessToken,
    hasError,
    formErrorMessage,
    fraudErrorMessage,
    paymentFailErrorMessage,
    paymentPendingErrorMessage,
    onLoadIframe,
    onCleanUpIframe,
    onCharge,
    onChargeSuccess,
    onChargeFail,
    onPaymentMethodChange,
    onRefreshDefaultPaymentMethod,
    isPaymentSummaryDefaultPaymentMethodSuccess,
    isEligibleForLeadGuarantee,
    isInitiateChargeFailedValidPaymentExists,
    initiateCardCaptureErrorMessage,
    verifyPaymentAndActivateListingErrorMessage,
    isRenderedInPageView,
    hostApp,
    user, // eslint-disable-line no-unused-vars
    price,
    listing,
    // unit test props hack
    currentPhase: pCurrentPhase,
}) => {
    const { isInMobileWebView } = React.useContext(RMWContext);
    const dispatch = useDispatch();

    const [currentPhase, setCurrentPhase] = React.useState(pCurrentPhase);
    /* eslint-disable react-hooks/exhaustive-deps */
    React.useEffect(() => {
        if (currentPhase === PaidInclusionConstants.PAYMENT_PHASE.CREDIT_CARD_CAPTURE) {
            if (!apiAccessToken.capture) {
                dispatch(PaidInclusionActions.initiateCreditCardCapture(listing, selectedProductType));
            }
        } else if (currentPhase === PaidInclusionConstants.PAYMENT_PHASE.CREDIT_CARD_CHARGE) {
            if (!apiAccessToken.charge && !isInitiateChargeFailedValidPaymentExists) {
                dispatch(PaidInclusionActions.initiateCreditCardCharge(listing, selectedProductType));
            }
        }
    }, [currentPhase, apiAccessToken.capture, apiAccessToken.charge]);

    React.useEffect(() => {
        if (hasError) {
            if (!formErrorMessage) {
                setCurrentPhase(PaidInclusionConstants.PAYMENT_PHASE.CAUGHT_ERROR);
            }
        } else if (isCaptured) {
            setCurrentPhase(PaidInclusionConstants.PAYMENT_PHASE.CREDIT_CARD_CHARGE);
        } else if (defaultPaymentMethod) {
            setCurrentPhase(
                defaultPaymentMethod.defaultPaymentMethodExists
                    ? PaidInclusionConstants.PAYMENT_PHASE.SELECT_PAYMENT_METHOD
                    : PaidInclusionConstants.PAYMENT_PHASE.CREDIT_CARD_CAPTURE
            );
        } else if (defaultPaymentMethodSuccess === false) {
            setCurrentPhase(PaidInclusionConstants.PAYMENT_PHASE.SELECT_PAYMENT_METHOD);
        }
    }, [hasError, formErrorMessage, fraudErrorMessage, isCaptured, defaultPaymentMethod, defaultPaymentMethodSuccess]);

    const renderEmptyDiv = () => (
        <div />
    );

    // get modal title via ref
    let modalDialogHeaderEl = renderEmptyDiv();

    if (typeof document !== 'undefined') {
        modalDialogHeaderEl = isInMobileWebView === false
            // defined in ModalContainer via constellation component
            ? document.querySelector('[class^="DialogHeader"] [class^="Text"]')
            // defined in PaymentPage
            : document.querySelector('.PaymentPage-Title');
    }

    const handlePaymentMethodChange = (method) => {
        if (method === PaidInclusionConstants.PAYMENT_METHOD.NEW
            || method === PaidInclusionConstants.PAYMENT_METHOD.DEFAULT) {
            setCurrentPhase(PaidInclusionConstants.PAYMENT_PHASE.CREDIT_CARD_CAPTURE);
        } else {
            setCurrentPhase(PaidInclusionConstants.PAYMENT_PHASE.CREDIT_CARD_CHARGE);
        }
        // fire GA events etc
        onPaymentMethodChange(method);
    };

    // if defaultPaymentMethodSuccess is false in the paymentSummary response, show select payment modal with new
    // selected.
    React.useEffect(() => {
        if (isPaymentSummaryDefaultPaymentMethodSuccess !== null && !isPaymentSummaryDefaultPaymentMethodSuccess) {
            handlePaymentMethodChange(PaidInclusionConstants.PAYMENT_METHOD.NEW);
        }
    }, [isPaymentSummaryDefaultPaymentMethodSuccess]);

    // display select payment modal if defaultPaymentMethod failed or if defaultPaymentMethod succeeded but the user
    // doesn't have a saved card. Otherwise just display CreditCardCapture modal.
    const displaySelectPaymentMethodModal =
        (defaultPaymentMethodSuccess !== null && defaultPaymentMethodSuccess === false)
        || (defaultPaymentMethodSuccess && defaultPaymentMethod.defaultPaymentMethodExists);

    switch (currentPhase) {
        case PaidInclusionConstants.PAYMENT_PHASE.SELECT_PAYMENT_METHOD:
        case PaidInclusionConstants.PAYMENT_PHASE.CREDIT_CARD_CAPTURE:
            return (
                <SelectPaymentMethod
                    defaultPaymentMethodSuccess={defaultPaymentMethodSuccess}
                    defaultCreditCard={defaultPaymentMethod}
                    onPaymentMethodChange={handlePaymentMethodChange}
                    isIframeLoaded={isFormLoaded}
                    onRefreshDefaultPaymentMethod={onRefreshDefaultPaymentMethod}
                    setCurrentPhase={setCurrentPhase}
                    displaySelectPaymentMethodModal={displaySelectPaymentMethodModal}
                    isRenderedInPageView={isRenderedInPageView}
                    price={price}
                    isEligibleForLeadGuarantee={isEligibleForLeadGuarantee}
                    creditCardCapture={(
                        <CreditCardCapture
                            price={price}
                            apiAccessToken={apiAccessToken.capture}
                            onLoadIframe={onLoadIframe}
                            onCleanUpIframe={onCleanUpIframe}
                            isIframeLoaded={isFormLoaded}
                            isCapturing={isCapturing}
                            isCaptured={isCaptured}
                            isEligibleForLeadGuarantee={isEligibleForLeadGuarantee}
                            formErrorMessage={formErrorMessage}
                            displayedInSelectPaymentMethodModal={displaySelectPaymentMethodModal}
                        />
                    )}
                />
            );
        case PaidInclusionConstants.PAYMENT_PHASE.CREDIT_CARD_CHARGE:
            return (
                <CreditCardCharge
                    apiAccessToken={apiAccessToken.charge}
                    onCharge={onCharge}
                    onChargeSuccess={onChargeSuccess}
                    onChargeFail={onChargeFail}
                    isCharging={isCharging}
                    isCharged={isCharged}
                    isInitiateChargeFailedValidPaymentExists={isInitiateChargeFailedValidPaymentExists}
                    isRenderedInPageView={isRenderedInPageView}
                    hostApp={hostApp}
                    modalDialogHeaderEl={modalDialogHeaderEl}
                />
            );
        case PaidInclusionConstants.PAYMENT_PHASE.CAUGHT_ERROR:
            return (
                <PaidInclusionError
                    fraudErrorMessage={fraudErrorMessage}
                    paymentFailErrorMessage={paymentFailErrorMessage}
                    paymentPendingErrorMessage={paymentPendingErrorMessage}
                    initiateCardCaptureErrorMessage={initiateCardCaptureErrorMessage}
                    verifyPaymentAndActivateListingErrorMessage={verifyPaymentAndActivateListingErrorMessage}
                />
            );
        default:
    }

    return <SpinnerContainer />;
};

PaidInclusionPaymentModal.defaultProps = {
    defaultPaymentMethod: null,
    isPaymentSummaryDefaultPaymentMethodSuccess: null,
    isEligibleForLeadGuarantee: false,
    user: {},
    onPaymentMethodChange: () => {},
    currentPhase: '',
};

PaidInclusionPaymentModal.propTypes = {
    defaultPaymentMethod: PropTypes.shape({
        defaultPaymentMethodExists: PropTypes.bool,
    }),
    isFormLoaded: PropTypes.bool.isRequired,
    isCapturing: PropTypes.bool.isRequired,
    isCaptured: PropTypes.bool.isRequired,
    isCharging: PropTypes.bool.isRequired,
    isCharged: PropTypes.bool.isRequired,
    apiAccessToken: PropTypes.shape({
        capture: PropTypes.string,
        charge: PropTypes.string,
    }).isRequired,
    hasError: PropTypes.bool.isRequired,
    formErrorMessage: PropTypes.string.isRequired,
    fraudErrorMessage: PropTypes.string.isRequired,
    paymentFailErrorMessage: PropTypes.string.isRequired,
    paymentPendingErrorMessage: PropTypes.string.isRequired,
    onLoadIframe: PropTypes.func.isRequired,
    onCleanUpIframe: PropTypes.func.isRequired,
    onCharge: PropTypes.func.isRequired,
    onChargeSuccess: PropTypes.func.isRequired,
    onChargeFail: PropTypes.func.isRequired,
    onPaymentMethodChange: PropTypes.func,
    isPaymentSummaryDefaultPaymentMethodSuccess: PropTypes.bool,
    isEligibleForLeadGuarantee: PropTypes.bool,
    user: PropTypes.object,
    currentPhase: PropTypes.string,
};

const mapStateToProps = state => ({
    defaultPaymentMethodSuccess: PaidInclusionSelectors.makeDefaultPaymentMethodSuccess(state),
    defaultPaymentMethod: PaidInclusionSelectors.makeDefaultPaymentMethod(state),
    isPaymentSummaryDefaultPaymentMethodSuccess:
        PaidInclusionSelectors.makeIsPaymentSummaryDefaultPaymentMethodSuccess(state),
    isFormLoaded: PaidInclusionSelectors.makeIsFormLoaded(state),
    isCapturing: PaidInclusionSelectors.makeIsCapturing(state),
    selectedProductType: PropertiesSelectors.makeCurrentProperty(state).paidInclusionSelectedProductType,
    isCaptured: PaidInclusionSelectors.makeIsCaptured(state),
    isCharging: PaidInclusionSelectors.makeIsCharging(state),
    isCharged: PaidInclusionSelectors.makeIsCharged(state),
    isInitiateChargeFailedValidPaymentExists: PaidInclusionSelectors.makeIsInitiateChargeFailedValidPaymentExists(
        state
    ),
    initiateCardCaptureErrorMessage: PaidInclusionSelectors.makeInitiateCardCaptureErrorMessage(state),
    apiAccessToken: PaidInclusionSelectors.makeApiAccessToken(state),
    hasError: PaidInclusionSelectors.makeHasError(state),
    formErrorMessage: PaidInclusionSelectors.makeFormErrorMessage(state),
    fraudErrorMessage: PaidInclusionSelectors.makeFraudErrorMessage(state),
    paymentFailErrorMessage: PaidInclusionSelectors.makePaymentFailErrorMessage(state),
    verifyPaymentAndActivateListingErrorMessage: PaidInclusionSelectors.makeVerifyPaymentAndActivateListingErrorMessage(
        state
    ),
    paymentPendingErrorMessage: PaidInclusionSelectors.makePaymentPendingErrorMessage(state),
    user: UserSelectors.makeUser(state),
});

const mapDispatchToProps = (dispatch, ownProps) => ({
    onLoadIframe: token => dispatch(PaidInclusionActions.loadCreditCardIframe(token, ownProps.listing)),
    onCleanUpIframe: () => dispatch(PaidInclusionActions.cleanupCreditCardIframe(ownProps.listing, ownProps.user)),
    onCharge: token => dispatch(PaidInclusionActions.performCharge(token, ownProps.listing, ownProps.user)),
    onChargeSuccess: () => dispatch(PaidInclusionActions.verifyPaymentAndActivateListing(ownProps.listing, false, true,
        ownProps.hostApp)).then(ownProps.handleVerifyPaymentAndActivateListingResponse),
    onRefreshDefaultPaymentMethod: () => dispatch(PaidInclusionActions.getDefaultPaymentMethod(ownProps.listing)),
});

export { PaidInclusionPaymentModal };
export default connect(mapStateToProps, mapDispatchToProps)(PaidInclusionPaymentModal);
